import { withSearch } from '@elastic/react-search-ui';
import { css } from '@linaria/core';
import Button from './Button';
import CloseIcon from '../icons/Close';
import { format, parseISO } from 'date-fns';
import { useParent } from '../contexts/ParentContext';
import {
  CLEAR_ALL_PRESERVED_FILTERS,
  isHiddenAppliedFilter,
  isUiOnlyFilter,
} from '../utils/uiFilters';
import { isMapFilter } from '../utils/mapFilters';

const wrapper = css`
  border-bottom: 1px solid #e5dbd1;
  padding-bottom: 20px;
  margin-bottom: 20px;

  a {
    font-size: 18px;
    line-height: 28px;
  }
`;

const buttons = css`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;

  button {
    font-size: 16px;
    height: auto;
    line-height: 24px;
    padding: 2px 15px;

    svg {
      flex-shrink: 0;
      height: 12px;
    }
  }
`;

const SHAPE_FILTER_FIELDS = [
  'mapShapeType',
  'mapShapeRadius',
  'mapShapePoints',
];

function AppliedFilters({ filters, clearFilters, removeFilter }) {
  const { parent } = useParent();
  const visibleFilters = filters.filter(
    f =>
      !isUiOnlyFilter(f.field) &&
      !isMapFilter(f.field) &&
      !isHiddenAppliedFilter(f.field),
  );
  // A drawn shape shows as one "Selected area" chip that clears all three
  // shape filter fields.
  const hasShapeFilter = filters.some(f => f.field === 'mapShapeType');
  const yearToEra = year => {
    return !year || year > 0 ? 'AD' : 'BC';
  };

  if (visibleFilters.length === 0 && !hasShapeFilter) {
    return null;
  }

  return (
    <div className={wrapper}>
      <div className={buttons}>
        {hasShapeFilter && (
          <Button
            EndIcon={CloseIcon}
            onClick={() => SHAPE_FILTER_FIELDS.forEach(f => removeFilter(f))}
            variant="primary"
          >
            Selected area
          </Button>
        )}
        {visibleFilters.map(filter => {
          const filterValues = filter.values
            .map(value => {
              const values = [];

              switch (filter.field) {
                case 'dateAmendedFrom':
                case 'dateFrom':
                  values.push({
                    removeCallback: () => removeFilter(filter.field),
                    label: `From: ${format(parseISO(value), 'd/M/y')}`,
                  });
                  break;
                case 'dateAmendedTo':
                case 'dateTo':
                  values.push({
                    removeCallback: () => removeFilter(filter.field),
                    label: `To: ${format(parseISO(value), 'd/M/y')}`,
                  });
                  break;
                case 'periodDateMin':
                case 'monumentDateMin':
                  values.push({
                    removeCallback: () => removeFilter(filter.field),
                    label: `From: ${Math.abs(value)} ${yearToEra(value)}`,
                  });
                  break;
                case 'periodDateMax':
                case 'monumentDateMax':
                  values.push({
                    removeCallback: () => removeFilter(filter.field),
                    label: `To: ${Math.abs(value)} ${yearToEra(value)}`,
                  });
                  break;
                case 'grade':
                  values.push({
                    removeCallback: () => removeFilter(filter.field, value),
                    label: `Grade: ${value}`,
                  });
                  break;
                case 'image':
                  values.push({
                    removeCallback: () => removeFilter(filter.field, value),
                    label: value === 'true' ? 'With images' : 'Without images',
                  });
                  break;
                case 'comments':
                  values.push({
                    removeCallback: () => removeFilter(filter.field, value),
                    label:
                      value === 'true'
                        ? 'Results with comments or photos'
                        : 'Results with no comments or photos',
                  });
                  break;
                case 'newEntries':
                  values.push({
                    removeCallback: () => removeFilter(filter.field),
                    label: 'New Entries',
                  });
                  break;
                case 'parent':
                  if (parent) {
                    values.push({
                      removeCallback: () => removeFilter(filter.field),
                      label: `${parent.type}: ${parent.ref}`.trim(),
                    });
                  }
                  break;
                default:
                  values.push({
                    removeCallback: () => removeFilter(filter.field, value),
                    label: value,
                  });
              }

              return values;
            })
            .flat();

          return filterValues.map((value, index) => (
            <Button
              EndIcon={CloseIcon}
              key={index}
              onClick={value.removeCallback}
              variant="primary"
            >
              {value.label}
            </Button>
          ));
        })}
      </div>

      <a href="#" onClick={() => clearFilters(CLEAR_ALL_PRESERVED_FILTERS)}>
        Clear all filters
      </a>
    </div>
  );
}

export default withSearch(
  ({ filters, clearFilters, removeFilter, setFilter }) => ({
    filters,
    clearFilters,
    removeFilter,
    setFilter,
  }),
)(AppliedFilters);
